import {
  AI_BG_NEW,
  Aws_Partner,
  Cloud_Infra_Icon,
  DataBricks,
  IOA,
  Our_Product_Bg,
  P2,
  P3,
  P5,
  SpeedData_Logo,
  System_Infra_Icon,
  Tech_Partners_Icon,
} from "../../../assets";
import { Helmet, HelmetData } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  riseFrom,
  maskedLineReveal,
} from "../../../utils/animations";
const helmetData = new HelmetData({});

const why = [
  {
    icon: Tech_Partners_Icon,
    title: "Technology Integration Partners",
    text: "Integrate your data platform, analytics solution, or AI application with infrastructure. Embed analytics intelligence, connect event streams, and accelerate innovation with technical enablement and co-marketing support.",
  },
  {
    icon: Cloud_Infra_Icon,
    title: "Cloud & Infrastructure Partners",
    text: "Expand your cloud and infrastructure offerings with [AI] Platform. Access co-selling opportunities, marketplace distribution, revenue-sharing programs, and dedicated partner support.",
  },
  {
    icon: System_Infra_Icon,
    title: "System Integrators & Consultancies",
    text: "Deliver solutions to your enterprise clients. We provide technical enablement, co-selling support, and implementation guidance.",
  },
];

const infra = [
  {
    icon: P2,
    title: "GPU Infrastructure Partner",
    desc: "NVIDIA provides the GPU infrastructure powering AI model training, inference, and scalable AI workloads across the [AI] Platform.",
    link: "https://www.nvidia.com",
  },
  {
    icon: P3,
    title: "Enterprise Data Platform Partner",
    desc: "Tealium integrates with CORE to deliver validated customer and behavioural data into analytics pipelines and AI agent ecosystems.",
    link: "https://tealium.com",
  },
  {
    icon: Aws_Partner,
    title: "Cloud Infrastructure & Marketplace Partner",
    desc: "AWS supports cloud deployment, marketplace availability, and enterprise co-sell opportunities, simplifying procurement and scaling.",
    link: "https://aws.amazon.com",
  },
  {
    icon: DataBricks,
    title: "Data Lakehouse Platform Partner",
    desc: "Databricks integrates with [AI] Platform, allowing enterprises to accelerate analytics workloads without disrupting existing data architectures.",
    link: "https://www.databricks.com",
  },
  {
    icon: P5,
    title: "Cloud Data Platform Partner",
    desc: "Snowflake customers can connect data assets to [AI] Platform for high-performance analytics, AI reasoning, and enterprise intelligence.",
    link: "https://www.snowflake.com",
  },
  {
    icon: IOA,
    title: "Strategic Analytics Research Partner",
    desc: "Accredited by the Institute of Analytics (IoA) UK and globally recognised to showcase our commitment to quality, ethics, and innovation in analytics education.",
    link: "https://ioaglobal.org",
  },
  {
    icon: SpeedData_Logo,
    title: "APU Hardware Partner · Foundational",
    desc: "Speedata powers the Analytics Processing Units (APUs) behind [AI] Platform, enabling up to 80x faster analytics performance and up to 40% lower processing costs for enterprise workloads.",
    link: "/speedata",
    internal: true,
  },
];

export default function OurPartnersPage() {
  const pageRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        maskedLineReveal(pageRef.current.querySelector(".op-hero-heading"), {
          scrollTrigger: revealTrigger(".op-hero-heading"),
        });
        riseFrom(".op-hero-item", {
          delay: 0.2,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".op-hero-item", "top 85%"),
        });
        riseFrom(".op-hero-media", {
          y: 40,
          duration: 1.1,
          delay: 0.3,
          scrollTrigger: revealTrigger(".op-hero-media", "top 85%"),
        });
        riseFrom(".op-infra-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".op-infra-head"),
        });
        riseFrom(".op-infra-grid", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".op-infra-grid", "top 75%"),
        });
        riseFrom(".op-why-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".op-why-head"),
        });
        riseFrom(".op-why-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".op-why-grid", "top 75%"),
        });
        riseFrom(".op-cta-card > *", {
          stagger: 0.12,
          scrollTrigger: revealTrigger(".op-cta-card", "top 80%"),
        });
      });
      return () => mm.revert();
    },
    { scope: pageRef }
  );

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>
          Our Partners — Technology &amp; Cloud Ecosystem | [AI]
        </title>
        <link
          rel="canonical"
          href="https://analyticsintelligence.com/partners"
        />
        <meta
          name="description"
          content="Analytics Intelligence partners with NVIDIA, AWS, Tealium, Databricks, Snowflake, and Speedata to power enterprise-scale AI and data infrastructure worldwide."
        />
        <meta
          name="keywords"
          content="Analytics Intelligence partners, NVIDIA partner, AWS partner, Tealium partner, Databricks, Snowflake, Speedata APU, Institute of Analytics UK, technology partners, enterprise AI ecosystem"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta
          property="og:title"
          content="Our Partners — Technology & Cloud Ecosystem | [AI]"
        />
        <meta
          property="og:description"
          content="A partner ecosystem built for enterprise scale — NVIDIA, AWS, Tealium, Databricks, Snowflake, and Speedata power Analytics Intelligence's AI and data infrastructure."
        />
        <meta
          property="og:url"
          content="https://analyticsintelligence.com/partners"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta
          name="twitter:title"
          content="Our Partners — Technology & Cloud Ecosystem | [AI]"
        />
        <meta
          name="twitter:description"
          content="A partner ecosystem built for enterprise scale — NVIDIA, AWS, Tealium, Databricks, Snowflake, and Speedata power Analytics Intelligence's AI and data infrastructure."
        />
        <meta
          name="twitter:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Our Partners | Analytics Intelligence",
            "description": "The technology, cloud, and data platform partners that power Analytics Intelligence — including NVIDIA, Tealium, AWS, Databricks, Snowflake, the Institute of Analytics (UK), and Speedata.",
            "url": "https://analyticsintelligence.com/partners",
            "publisher": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "alternateName": ["Analytics Intelligence", "[AI]"],
              "url": "https://analyticsintelligence.com"
            }
          }
        `}</script>
      </Helmet>
      <main ref={pageRef}>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center"
        >
          <div className="container mx-auto w-full h-full pt-[12rem] pb-[96px]">
            <div className="flex flex-col items-center gap-16">
              <div className="flex flex-col gap-4 items-center">
                <span className="op-hero-item text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  Partner Ecosystem
                </span>
                <div className="flex flex-col items-center gap-16">
                  <h2
                    className="op-hero-heading text-[38px] md:text-[48px] text-center font-extrabold leading-[1.3] md:leading-[54px] text-white max-w-[820px]"
                    dangerouslySetInnerHTML={{
                      __html: "A Partner Ecosystem Built for Enterprise Scale.",
                    }}
                  />

                  <div className="flex flex-col-reverse md:flex-row items-center gap-8">
                    <div className="flex-1 flex items-center justify-center">
                      <img
                        src="https://res.cloudinary.com/analytics-intelligence/image/upload/v1781592888/website/Partnership-amico_1_wpdykj.png"
                        alt="Data Structures"
                        className="op-hero-media w-full md:max-w-[400px] h-auto"
                      />
                    </div>

                    <p className="op-hero-item text-sm md:text-base text-white text-center md:text-left flex-1 max-w-[600px]">
                      [AI] Analytics Intelligence works with the technology,
                      cloud, and data platform companies that define enterprise
                      data infrastructure worldwide, to deliver accelerated
                      analytics and intelligence capabilities to more
                      organisations, at greater scale.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#010127] relative pt-[48px] pb-[96px] md:py-[96px]">
          <div className="container w-full flex flex-col gap-11">
            <div className="w-[150px] lg:w-[400px] h-[300px] bg-[#0073FF]/50 blur-[150px] absolute top-[30%] left-[50%] translate-x-[-50%] translate-y-[-50%]"></div>

            <div className="op-infra-head flex flex-col items-center text-center md:text-left z-[800]">
              <span className="text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                Technology & Infrastructure Partners
              </span>
              <h2 className="text-white font-extrabold text-[24px] md:text-[32px] text-center">
                The companies that power [AI]
              </h2>
            </div>
            <div className="op-infra-grid mt-0 md:mt-11 gap-6 md:gap-12 grid grid-cols-1 md:grid-cols-12">
              {infra.map((item, idx) => {
                const cardClassName = `flex flex-col items-start gap-3 col-span-12 md:col-span-6 lg:col-span-4 bg-[#40406C80] backdrop-blur-sm p-6 rounded-xl transition-transform duration-300 ease-in hover:scale-[1.02] hover:bg-[#40406C99] ${
                  idx === infra.length - 1
                    ? "md:col-start-4 lg:col-start-5"
                    : ""
                }`;
                const cardContent = (
                  <>
                    <img
                      src={item.icon}
                      className="max-h-[44px]"
                      alt={item.title}
                    />
                    <p className="text-white font-extrabold text-lg md:text-xl">
                      {item.title}
                    </p>
                    <p className="text-white text-base">{item.desc}</p>
                  </>
                );

                return item.internal ? (
                  <Link key={idx} to={item.link} className={cardClassName}>
                    {cardContent}
                  </Link>
                ) : (
                  <a
                    key={idx}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cardClassName}
                  >
                    {cardContent}
                  </a>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-[#EDF1FF] py-[48px]">
          <div className="container space-y-12">
            <div className="op-why-head flex flex-col items-center text-center md:text-left z-[800]">
              <span className="text-sm md:text-base bg-[#C7E0FF] px-3 p-[6px] rounded-[100px] text-[#020128">
                Partnership
              </span>
              <h2 className="text-[#020128] font-extrabold text-[24px] md:text-[32px] text-center">
                Multiple ways to partner with [AI]
              </h2>
            </div>

            <div className="op-why-grid grid grid-cols-12 gap-y-6 md:gap-11">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="op-why-card group col-span-12 md:col-span-6 lg:col-span-4 bg-white hover:bg-poppingBlue flex flex-col items-center rounded-xl px-3 py-6 gap-6 transition-colors duration-700"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16 transition-all duration-700 group-hover:brightness-0 group-hover:invert"
                  />
                  <div className="space-y-1">
                    <h4 className="text-center text-lg lg:text-xl xl:text-2xl font-bold text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.title}
                    </h4>

                    <h5 className="text-center md:text-left text-[#0073FF] text-base md:text-lg font-bold transition-colors duration-700 group-hover:text-white">
                      {item.subtitle}
                    </h5>

                    <p className="text-center text-sm md:text-base text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="relative pt-[96px] md:pt-[116px] pb-10"
          style={{
            backgroundImage: `url("${Our_Product_Bg}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="container">
            <div className="op-cta-card bg-[#1E1E4080] backdrop-blur-[20px] flex flex-col items-center gap-6 px-4 md:px-8 py-8 sm:py-12 md:py-14 lg:py-16 xl:py-20 rounded-3xl">
              <div className="">
                <h2 className="text-[32px] md:text-[44px] leading-[1.3] text-center font-extrabold text-white">
                  Become a partner
                </h2>
                <p className="text-sm md:text-base text-center text-[#FFFFFF] max-w-[800px]">
                  [AI] is building the infrastructure layer for the agentic
                  analytics era. Whether you are a cloud provider, data
                  platform, hardware manufacturer, or system integrator, there
                  is a partnership model for working together.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-y-3 sm:gap-y-0 gap-x-6 w-full justify-center">
                <Link to="/contact">
                  <button className="text-sm md:text-base font-semibold py-3 px-4 md:px-6 sm:min-w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                    Become a Partner
                  </button>
                </Link>

                <Link to="/contact">
                  <button className="text-sm md:text-base font-semibold py-3 px-4 md:px-6 sm:min-w-[170px] text-center btn-sweep btn-sweep-white text-poppingBlue rounded-[100px] gap-2">
                    Contact Us
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
